import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { X, Send, Mail, Users, Loader2 } from "lucide-react";

export function AskSomeoneModal({ onClose, contextNote = "" }) {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [to, setTo] = useState("");
  const [message, setMessage] = useState(contextNote);
  const [sending, setSending] = useState(false);
  const [myEmail, setMyEmail] = useState(user?.contact_email || "");
  const [savedEmail, setSavedEmail] = useState(user?.contact_email || "");
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => { api.get("/family/members").then((r) => setMembers(r.data)).catch(() => setMembers([])); }, []);

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      const { data } = await api.put("/me/profile", { contact_email: myEmail.trim() });
      setSavedEmail(data.contact_email);
      toast.success("Contact email saved.");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSavingEmail(false); }
  };

  const send = async () => {
    if (!to) return toast.error("Pick who you want to ask.");
    if (!message.trim()) return toast.error("Type your question first.");
    setSending(true);
    try {
      const { data } = await api.post("/messages/ask", { to_user_id: to, message });
      toast.success(`Sent to ${data.to}! They'll get your message by email.`);
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const needsEmail = !savedEmail;

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={() => !sending && onClose()}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        data-testid="ask-someone-modal" className="w-full max-w-md rounded-3xl bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <span className="flex items-center gap-2 font-head font-semibold text-[#3D405B]"><Users size={18} className="text-[#E07A5F]" /> Ask Someone</span>
          <button data-testid="ask-close" onClick={onClose} className="text-[#6B7280] hover:text-[#3D405B]"><X size={20} /></button>
        </div>

        {/* Your contact email */}
        <div className={`rounded-2xl border p-3 mb-4 ${needsEmail ? "border-[#F4A261]/50 bg-[#F4A261]/10" : "border-[#EAE6DF] bg-[#F7F5F0]"}`}>
          <label className="text-xs font-medium text-[#3D405B] flex items-center gap-1.5 mb-1.5"><Mail size={13} /> Your contact email {needsEmail && <span className="text-[#E07A5F]">— set this so replies reach you</span>}</label>
          <div className="flex gap-2">
            <input data-testid="ask-my-email" value={myEmail} onChange={(e) => setMyEmail(e.target.value)} placeholder="you@example.com"
              className="flex-1 rounded-full border border-[#EAE6DF] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]" />
            <button data-testid="ask-save-email" onClick={saveEmail} disabled={savingEmail || myEmail.trim() === savedEmail}
              className="rounded-full bg-[#3D405B] text-white px-4 py-2 text-sm font-medium hover:bg-[#2c2f45] transition-colors disabled:opacity-50">{savingEmail ? "…" : "Save"}</button>
          </div>
        </div>

        <label className="text-sm font-medium text-[#3D405B]">Who do you want to ask?</label>
        <select data-testid="ask-recipient" value={to} onChange={(e) => setTo(e.target.value)}
          className="mt-1 mb-4 w-full rounded-xl border border-[#EAE6DF] bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]">
          <option value="">Choose a person…</option>
          {members.map((m) => (
            <option key={m.id} value={m.id} disabled={!m.has_email}>
              {m.first_name} {m.last_name} ({m.role}){!m.has_email ? " — no email yet" : ""}
            </option>
          ))}
        </select>

        <label className="text-sm font-medium text-[#3D405B]">Your question</label>
        <textarea data-testid="ask-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
          placeholder="Type your question here…" className="mt-1 w-full rounded-xl border border-[#EAE6DF] p-3 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F] resize-y" />

        <button data-testid="ask-send" onClick={send} disabled={sending || needsEmail}
          className="mt-4 w-full flex items-center justify-center gap-2 rounded-full bg-[#E07A5F] text-white py-3 font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} {sending ? "Sending…" : "Send message"}
        </button>
        {needsEmail && <p className="text-xs text-[#6B7280] mt-2 text-center">Save your contact email above before sending.</p>}
      </motion.div>
    </div>
  );
}
